import { useState } from 'react';
import { Badge, Popover, type MantineSize } from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import { DatePicker } from '@mantine/dates';

interface DateChipProps {
  /** Текущее значение даты */
  value: string | Date | null | undefined;
  /** Метка чипа («Срок», «ДР» и т.п.) — показывается только когда дата не задана */
  label: string;
  /** Цвет чипа когда дата задана */
  color?: string;
  /** CSS-класс для ghost-режима (показывается по hover родителя) */
  hintClassName?: string;
  /** Вызывается после выбора даты */
  onSave: (date: Date | null) => void | Promise<void>;
  /** Размер Badge. По умолчанию 'xs'. */
  size?: MantineSize;
}

function formatRU(date: Date): string {
  return date.toLocaleDateString('ru-RU');
}

/**
 * Чип-пикер даты.
 * Когда дата задана — показывает цветной Badge с датой.
 * Когда не задана — показывает серый ghost-Badge с label (управляется через hintClassName + CSS hover).
 * Клик открывает Popover с DatePicker.
 */
export function DateChip({
  value,
  label,
  color = 'blue',
  hintClassName,
  onSave,
  size = 'xs',
}: DateChipProps) {
  const [opened, setOpened] = useState(false);

  const date = value ? (value instanceof Date ? value : new Date(value as string)) : null;
  const isValid = date !== null && !isNaN(date.getTime());

  const handleChange = (raw: Date | string | null) => {
    setOpened(false);
    if (!raw) {
      void onSave(null);
      return;
    }
    const d = raw instanceof Date ? raw : new Date(raw);
    if (!isNaN(d.getTime())) void onSave(d);
  };

  return (
    <Popover opened={opened} onChange={setOpened} withArrow={false} position='bottom-start'>
      <Popover.Target>
        {isValid ? (
          <Badge
            size={size}
            color={color}
            variant='light'
            radius='sm'
            leftSection={<IconCalendar size={12} />}
            style={{ cursor: 'pointer' }}
            onClick={e => {
              e.stopPropagation();
              setOpened(true);
            }}
          >
            {formatRU(date!)}
          </Badge>
        ) : (
          <Badge
            size={size}
            color='gray'
            variant='light'
            radius='sm'
            leftSection={<IconCalendar size={12} />}
            className={hintClassName}
            style={{
              opacity: hintClassName ? 0 : 1,
              transition: 'opacity 0.2s ease',
              cursor: 'pointer',
            }}
            onClick={e => {
              e.stopPropagation();
              setOpened(true);
            }}
          >
            {label}
          </Badge>
        )}
      </Popover.Target>
      <Popover.Dropdown p='xs'>
        <DatePicker value={isValid ? date : null} onChange={handleChange} />
      </Popover.Dropdown>
    </Popover>
  );
}
